import { assert, defined, definedMap, mapFilterUndefined } from "@glideapps/ts-necessities";
import { getLocalizedString } from "common/components/localization";
import type { AppDescription } from "common/components/SerializedApp";
import { AppKind, getAppTabs } from "common/components/SerializedApp";
import { generateImageFromSeed } from "common/components/triangle-image";
import type { AppFacilities, ComputationModel, DataStore } from "common/components/types";
import { getUserProfileTableInfo } from "common/components/user-profile";
import type { GroundValue, Row, Table } from "common/computation-model/data";
import { asMaybeString, asRow } from "common/computation-model/data";
import type { RootPath } from "common/computation-model/paths";
import type { TableName } from "common/database-strings";
import { getDocURL } from "common/docUrl";
import { handlerForArrayScreenFormat } from "common/generator/array-screens";
import type {
    ContextTableTypes,
    ExistingAppDescriptionContext,
    InternalScreenContext,
    WireActionRunner,
    WireComponentHydratorWithID,
    WirePredicate,
    WireRowComponentHydrationBackend,
    WireTableComponentHydrator,
    WireValueGetter,
} from "common/generator/components";
import { Appearance } from "common/generator/components/button-appearance";
import { Corners } from "common/generator/components/image-types";
import { Mood } from "common/generator/components/mood";
import { PageScreenPosition } from "common/generator/components/parsed-path";
import { getScreenProperty, makeSpecialValueProperty } from "common/generator/components/property-utils";
import type {
    ArrayScreenDescription,
    ClassScreenDescription,
    FilterArrayTransform,
    InputOutputTables,
    TableGlideType,
} from "common/generator/description";
import {
    ArrayTransformKind,
    getScreenComponents,
    getTableName,
    isAddOrEditOrFormScreenDescription,
    isSingleRelationType,
    isStringTypeKind,
    makeInputOutputTables,
    MutatingScreenKind,
    ScreenDescriptionKind,
    SourceColumnKind,
    SpecialValueKind,
    userProfileScreenName,
} from "common/generator/description";
import { getColumnAssignments, makeInputOutputTablesForClassOrArrayScreen } from "common/generator/description-utils";
import { getEditOnSubmitActions } from "common/generator/form-on-submit";
import { decomposePredicateCombinationFormula } from "common/generator/formulas/formula-specifications";
import { rowIndexColumnName } from "common/google-sheets";
import { makeRowID } from "common/make-row-id";
import { filterType, nullToUndefined } from "common/Support";
import type { WireComponent } from "common/wire";
import { WireComponentKind } from "common/wire/component-kind";
import type { WireAction } from "common/wire/types";
import { HeroImageEffect, HeroSize, HeroStyle } from "common/wire/types";
import {
    inflateActions,
    inflateColumnAssignments,
    inflateComponent,
    isNotEmptyDisplayValue,
    registerActionRunner,
} from "common/wire/utils";
import { getMutatingKindForScreen } from "lib/mutating-screen-kind";
import fromPairs from "lodash/fromPairs";
import type { InternalTabHydrator } from "./hydration";
import { RowValueProviderMaker } from "./hydration";
import { InflationBackend } from "./inflation";
import {
    addSubscriptionHandler,
    emptyInternalScreenContext,
    makeInternalScreenContext,
    makeTableSubscriptionSources,
} from "./internal";
import type { ScreenHydrationContext, SubscriptionInfo, TableTransformer } from "./internal-types";
import { RowSubscriptionHandler, TableSubscriptionHandler } from "./subscription";

type ScreenContextGetter = (
    hydrationContext: ScreenHydrationContext,
    oldSubscriptionInfo: SubscriptionInfo | undefined
) => [InternalScreenContext, SubscriptionInfo | undefined];

type SpecialButtonHydrator = (hb: WireRowComponentHydrationBackend, isEditValid: boolean) => WireComponent | undefined;

interface InflatedClassScreenCommon {
    readonly tables: InputOutputTables;
    readonly componentHydrators: WireComponentHydratorWithID[];
    readonly titleGetter: WireValueGetter;
    readonly transformTable: TableTransformer;
}

interface InflatedClassScreen {
    readonly tables: InputOutputTables;
    readonly componentHydrators: WireComponentHydratorWithID[];
    readonly specialButtonHydrators: readonly SpecialButtonHydrator[];
    readonly titleGetter: WireValueGetter;

    readonly getScreenContext: ScreenContextGetter;
}

interface InflatedArrayScreen {
    readonly table: TableGlideType;
    readonly contentHydrator: WireTableComponentHydrator;

    getContextTable(
        hydrationContext: ScreenHydrationContext,
        oldSubscriptionInfo: SubscriptionInfo | undefined
    ): [Table, SubscriptionInfo] | undefined;
}

interface InflatedUserProfile {
    readonly emailValueGetter: WireValueGetter;
    readonly nameValueGetter: WireValueGetter | undefined;
}

const dummyUserProfileTable: TableGlideType = {
    name: "$userProfiles",
    columns: [],
};
const dummyUserProfileTables: ContextTableTypes = {
    ...makeInputOutputTables(dummyUserProfileTable),
    containingScreen: undefined,
};

function inflateFilters(ib: InflationBackend | undefined, filters: readonly FilterArrayTransform[]): WirePredicate {
    if (ib !== undefined) {
        return ib.inflateFilters(filters, undefined);
    } else {
        for (const filterTransform of filters) {
            if (filterTransform.isActive === false) continue;
            const condition = decomposePredicateCombinationFormula(filterTransform.predicate)?.spec;
            if (condition === undefined || condition.predicates.length > 0 || condition.combinator !== "and") {
                return () => false;
            }
        }
        return () => true;
    }
}

export interface ScreenInflatorCallbacks {
    readonly closeModalAction: WireAction;

    // This has to be callable without a `this`.
    onDataChange(): void;
    setColumnsInInvisibleRow(tableName: TableName, row: Row, update: Record<string, GroundValue | null>): void;
    retireSubscriptionInfo(si: SubscriptionInfo): void;
}

export class ScreenInflator {
    // ##cleanUpInflatedStuff:
    // These all have to be cleaned up when the computation model changes.
    private readonly inflatedClassScreenCommons = new Map<string, InflatedClassScreenCommon>();
    private readonly inflatedClassScreens = new Map<string, InflatedClassScreen>();
    private readonly inflatedArrayScreens = new Map<string, InflatedArrayScreen>();
    private inflatedUserProfile: InflatedUserProfile | undefined;
    private inflatedInternalTabs: readonly InternalTabHydrator[] | undefined;
    private readonly inflatedScreenTitles = new Map<
        string,
        readonly [WireValueGetter, InputOutputTables | undefined]
    >();

    constructor(
        public readonly appID: string,
        private readonly appFacilities: AppFacilities,
        private readonly dataStore: DataStore,
        private readonly context: ExistingAppDescriptionContext,
        public readonly isBuilder: boolean,
        private readonly computationModel: ComputationModel | undefined,
        private readonly callbacks: ScreenInflatorCallbacks
    ) {}

    private get appKind(): AppKind {
        return AppKind.Page;
    }

    private get appDescription(): AppDescription {
        return this.context.appDescription;
    }

    private getRowIndexRootPath(tableName: TableName): RootPath {
        const [[, rowIndexRootPath]] = defined(
            defined(this.computationModel).getColumnPaths(tableName)?.get(rowIndexColumnName)
        );
        return defined(rowIndexRootPath);
    }

    private makeInflationBackend(
        tables: ContextTableTypes,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): InflationBackend | undefined {
        if (this.computationModel === undefined) return undefined;

        return new InflationBackend(
            this.appFacilities,
            this.context,
            { ...tables, containingScreen: undefined },
            this.computationModel,
            mutatingScreenKind
        );
    }

    public inflateScreenTitle(screenName: string): readonly [WireValueGetter, InputOutputTables | undefined] {
        let tables: InputOutputTables | undefined = undefined;

        function makeUnboundResult(): readonly [WireValueGetter, InputOutputTables | undefined] {
            return [() => null, tables];
        }

        let titleGetter = this.inflatedScreenTitles.get(screenName);
        if (titleGetter !== undefined) {
            return titleGetter;
        }

        const fallbackTitle =
            screenName === userProfileScreenName ? getLocalizedString("profile", this.appKind) : undefined;

        const screen = this.context.appDescription.screenDescriptions[screenName];
        if (screen?.kind !== ScreenDescriptionKind.Class) {
            if (screenName === userProfileScreenName) {
                const tableName = getUserProfileTableInfo(this.context.appDescription)?.tableName;
                const table = this.context.findTable(tableName);
                return [() => fallbackTitle, definedMap(table, makeInputOutputTables)];
            }

            return makeUnboundResult();
        }

        tables = makeInputOutputTablesForClassOrArrayScreen(screen, t => this.context.findTable(t));
        if (tables === undefined) return makeUnboundResult();

        const mutatingScreenKind = getMutatingKindForScreen(screenName, screen);
        const ib = this.makeInflationBackend({ ...tables, containingScreen: undefined }, mutatingScreenKind);
        if (ib === undefined) return makeUnboundResult();

        const titleValueGetter = ib.getValueGetterForProperty(screen.title, true)[0];
        titleGetter = [
            hb => {
                const v = titleValueGetter(hb);
                if (isNotEmptyDisplayValue(v)) {
                    return v;
                } else {
                    return fallbackTitle;
                }
            },
            tables,
        ];
        this.inflatedScreenTitles.set(screenName, titleGetter);
        return titleGetter;
    }

    public inflateClassScreenCommon(
        screenName: string,
        screen: ClassScreenDescription
    ): InflatedClassScreenCommon | undefined {
        let common = this.inflatedClassScreenCommons.get(screenName);
        if (common !== undefined) return common;

        const [titleGetter, tables] = this.inflateScreenTitle(screenName);
        if (tables === undefined) return undefined;

        this.dataStore.fetchTableRows(getTableName(tables.input), false);

        const mutatingScreenKind = getMutatingKindForScreen(screenName, screen);
        const ib = this.makeInflationBackend({ ...tables, containingScreen: undefined }, mutatingScreenKind);
        if (ib === undefined) return undefined;

        const componentHydrators = mapFilterUndefined(getScreenComponents(screen), desc => inflateComponent(ib, desc));

        const transformTable = ib.inflateTransforms(tables.input, screen.transforms ?? []);

        common = { tables, componentHydrators, titleGetter, transformTable };
        this.inflatedClassScreenCommons.set(screenName, common);
        return common;
    }

    // Maybe this doesn't belong in here?
    public fetchScreenData(
        screenName: string,
        screen: ClassScreenDescription,
        hydrationContext: ScreenHydrationContext
    ): [InternalScreenContext, SubscriptionInfo] | undefined {
        const { computationModel } = this;
        if (computationModel === undefined) return undefined;

        const common = this.inflateClassScreenCommon(screenName, screen);
        if (common === undefined) return undefined;
        const { tables, transformTable } = common;

        const tableName = getTableName(tables.input);
        hydrationContext.fetchTableData(tableName);
        const tableData = computationModel.getBaseDataForTable(tableName);

        const sources = makeTableSubscriptionSources();
        const rvpm = new RowValueProviderMaker(hydrationContext, tableName, sources, undefined);

        const rows = definedMap(tableData, t => transformTable(rvpm, t)) ?? [];

        const rowIndexRootPath = this.getRowIndexRootPath(tableName);
        // TODO: We don't need to subscribe to the row index if
        // the transformer subscribes to any column.
        sources.columnsInDirectRows.get(rowIndexRootPath.rest.key).add(rowIndexColumnName);
        const handler = new TableSubscriptionHandler(sources, this.callbacks.onDataChange);
        const subscriptionPath = addSubscriptionHandler(computationModel.ns, "classScreenContext", handler);
        const subscriptionInfo: SubscriptionInfo = {
            handlerAndPath: {
                handler,
                path: subscriptionPath,
            },
            tokens: new Set(),
            needsParsedPath: false,
            subsidiaries: [],
        };

        let screenContext: InternalScreenContext;
        if (rows.length === 0) {
            screenContext = emptyInternalScreenContext;
        } else {
            screenContext = makeInternalScreenContext(rows[0], undefined, undefined);
        }

        return [screenContext, subscriptionInfo];
    }

    private inflateRegularClassScreen(
        screenName: string,
        screen: ClassScreenDescription
    ): InflatedClassScreen | undefined {
        const common = this.inflateClassScreenCommon(screenName, screen);
        if (common === undefined) return undefined;
        const { tables, componentHydrators, titleGetter } = common;

        const mutatingScreenKind = getMutatingKindForScreen(screenName, screen);
        const ib = this.makeInflationBackend({ ...tables, containingScreen: undefined }, mutatingScreenKind);
        if (ib === undefined) return undefined;

        const specialButtonHydrators: SpecialButtonHydrator[] = [];

        const outputTableName = getTableName(tables.output);

        if (
            mutatingScreenKind === MutatingScreenKind.EditScreen ||
            mutatingScreenKind === MutatingScreenKind.FormScreen
        ) {
            assert(isAddOrEditOrFormScreenDescription(screenName, screen));
            const outputValueGetters = inflateColumnAssignments(
                ib,
                outputTableName,
                getColumnAssignments(screen),
                true
            );
            const onSubmitHydrator = inflateActions(
                ib.makeInflationBackendForOutputTable(),
                getEditOnSubmitActions(screen)
            );

            specialButtonHydrators.push(
                (hb, isEditValid) => {
                    let action: WireAction | undefined;

                    if (isEditValid) {
                        const { rowContext: rowScreenContext } = hb;
                        assert(rowScreenContext !== undefined);
                        const { outputRow } = rowScreenContext;
                        assert(outputRow !== undefined);

                        let onSubmit: WireActionRunner | undefined;
                        if (onSubmitHydrator !== undefined) {
                            const ohb = hb.makeHydrationBackendForOutputRow();
                            if (ohb !== undefined) {
                                onSubmit = onSubmitHydrator(ohb);
                            }
                        }

                        const columnAssignmentValues = fromPairs(outputValueGetters?.map(([n, g]) => [n, g(hb)]));

                        let saveChanges: WireActionRunner;
                        if (mutatingScreenKind === MutatingScreenKind.EditScreen) {
                            saveChanges = ab => ab.saveEditScreenChanges(outputTableName, rowScreenContext);
                        } else {
                            saveChanges = ab => ab.addRow(outputTableName, outputRow);
                        }

                        action = registerActionRunner(hb, async ab => {
                            this.callbacks.setColumnsInInvisibleRow(outputTableName, outputRow, columnAssignmentValues);
                            const success = await saveChanges(ab);
                            if (!success) return false;
                            ab.closeScreen(PageScreenPosition.Modal);
                            return (await onSubmit?.(ab)) ?? true;
                        });
                    }

                    return {
                        kind: WireComponentKind.Button,
                        title:
                            mutatingScreenKind === MutatingScreenKind.EditScreen
                                ? "Save"
                                : getLocalizedString("submit", this.appKind),
                        icon: undefined,
                        appearance: Appearance.Filled,
                        action,
                    };
                },
                hb => {
                    if (hb.screenPosition !== PageScreenPosition.Modal) return undefined;
                    return {
                        kind: WireComponentKind.Button,
                        title: getLocalizedString("cancel", this.appKind),
                        icon: undefined,
                        appearance: Appearance.Bordered,
                        action: this.callbacks.closeModalAction,
                    };
                }
            );
        }

        return {
            tables,
            componentHydrators,
            specialButtonHydrators,
            titleGetter,
            getScreenContext: (hydrationContext, oldSubscriptionInfo) => {
                if (screen.fetchesData === true) {
                    // TODO: Don't get new context if subscription is not dirty.
                    // TODO: Don't make new subscription if we have one.

                    // TODO: I'm not sure this subscription stuff should be
                    // in here, or cloistered off somewhere else.
                    if (oldSubscriptionInfo !== undefined) {
                        this.callbacks.retireSubscriptionInfo(oldSubscriptionInfo);
                    }

                    // We've already done all the things that could fail in
                    // this function, so we're asserting that it doesn't.
                    return defined(this.fetchScreenData(screenName, screen, hydrationContext));
                } else {
                    return [hydrationContext.internalScreen.context, oldSubscriptionInfo];
                }
            },
        };
    }

    private inflateUserProfileScreen(screen: ClassScreenDescription | undefined): InflatedClassScreen | undefined {
        let tables: InputOutputTables;
        let componentHydrators: WireComponentHydratorWithID[];
        let getScreenContext: ScreenContextGetter;
        let titleGetter: WireValueGetter;

        if (screen !== undefined) {
            // We ignore the table transformer here because the user profile
            // screen isn't supposed to have transforms.
            const common = this.inflateClassScreenCommon(userProfileScreenName, screen);
            if (common === undefined) return undefined;

            tables = common.tables;
            componentHydrators = common.componentHydrators;
            titleGetter = common.titleGetter;

            getScreenContext = (_hydrationContext, oldSubscriptionInfo) => {
                if (oldSubscriptionInfo !== undefined) {
                    this.callbacks.retireSubscriptionInfo(oldSubscriptionInfo);
                }

                let row: Row | undefined;
                let subscriptionInfo: SubscriptionInfo | undefined;
                const { computationModel } = this;
                if (computationModel !== undefined) {
                    const path = computationModel.getUserProfileRowPath();
                    if (path !== undefined) {
                        const handler = new RowSubscriptionHandler(
                            {
                                globalKeys: new Set([path.rest.key]),
                                columnsInRows: new Map(),
                            },
                            this.callbacks.onDataChange
                        );
                        const subscriptionPath = addSubscriptionHandler(
                            computationModel.ns,
                            "userProfileScreenContext",
                            handler
                        );
                        subscriptionInfo = {
                            handlerAndPath: { handler, path: subscriptionPath },
                            tokens: new Set(),
                            needsParsedPath: false,
                            subsidiaries: [],
                        };
                        row = definedMap(computationModel.ns.get(path), asRow);
                    }
                }
                return [makeInternalScreenContext(row, undefined, undefined), subscriptionInfo];
            };
        } else {
            tables = dummyUserProfileTables;

            const ib = this.makeInflationBackend({ ...tables, containingScreen: undefined }, undefined);
            if (ib === undefined) return undefined;
            const [realEmailAddressGetter] = ib.getValueGetterForProperty(
                makeSpecialValueProperty(SpecialValueKind.RealEmailAddress),
                true
            );

            const heroHydrator: WireComponentHydratorWithID = [
                hb => {
                    const emailAddress = asMaybeString(nullToUndefined(realEmailAddressGetter(hb)));
                    const image = generateImageFromSeed(emailAddress ?? "", false);
                    return {
                        kind: WireComponentKind.Hero,
                        subtitle: emailAddress,
                        image,
                        backgroundEffect: HeroImageEffect.None,
                        withBlur: false,
                        bgStyle: HeroStyle.Card,
                        imageCorners: Corners.Rounded,
                        size: HeroSize.Medium,
                        actions: [],
                    };
                },
                makeRowID(),
            ];
            const hintHydrator: WireComponentHydratorWithID = [
                hb => {
                    const token = hb.registerAction(async ab => {
                        ab.actionCallbacks.openLink(getDocURL("userProfile"));
                        return true;
                    });
                    return {
                        kind: WireComponentKind.Hint,
                        description: "You haven't set up the User Profile table yet.",
                        mood: Mood.Neutral,
                        actionTitle: "Learn more",
                        action: { token },
                    };
                },
                makeRowID(),
            ];

            componentHydrators = [hintHydrator, heroHydrator];

            getScreenContext = () => [emptyInternalScreenContext, undefined];

            titleGetter = () => getLocalizedString("profile", this.appKind);
        }

        return {
            tables,
            getScreenContext,
            titleGetter,
            componentHydrators,
            specialButtonHydrators: [],
        };
    }

    public inflateClassScreen(
        screenName: string,
        screen: ClassScreenDescription | undefined
    ): InflatedClassScreen | undefined {
        let inflatedScreen = this.inflatedClassScreens.get(screenName);
        if (inflatedScreen === undefined) {
            if (screenName === userProfileScreenName) {
                inflatedScreen = this.inflateUserProfileScreen(screen);
            } else {
                inflatedScreen = this.inflateRegularClassScreen(screenName, defined(screen));
            }
            if (inflatedScreen === undefined) return undefined;
            this.inflatedClassScreens.set(screenName, inflatedScreen);
        }
        return inflatedScreen;
    }

    public inflateArrayScreen(screenName: string, screen: ArrayScreenDescription): InflatedArrayScreen | undefined {
        let inflatedScreen = this.inflatedArrayScreens.get(screenName);
        if (inflatedScreen !== undefined) return inflatedScreen;

        const handler = handlerForArrayScreenFormat(screen.format);
        if (handler === undefined) return undefined;

        if (!isSingleRelationType(screen.type)) return undefined;
        if (screen.fetchesData !== true) return undefined;

        const table = this.context.findTable(screen.type);
        if (table === undefined) return undefined;

        this.dataStore.fetchTableRows(getTableName(table), false);

        const { computationModel } = this;
        const ib = this.makeInflationBackend(
            { ...makeInputOutputTables(table), containingScreen: undefined },
            undefined
        );
        if (ib === undefined) return undefined;

        const contentHydrator = handler.inflateContent(ib, screen, undefined, undefined);
        if (contentHydrator === undefined) return undefined;

        inflatedScreen = {
            table,
            contentHydrator,
            getContextTable: (hydrationContext, oldSubscriptionInfo) => {
                if (oldSubscriptionInfo !== undefined) {
                    this.callbacks.retireSubscriptionInfo(oldSubscriptionInfo);
                }

                const tableName = getTableName(table);

                const tableData = computationModel?.getBaseDataForTable(tableName);
                if (tableData === undefined) return undefined;
                hydrationContext.fetchTableData(tableName);

                // TODO: Subscribe to whatever we're sorting/filtering by
                // once we do that and once we support array screens.
                const rowIndexRootPath = this.getRowIndexRootPath(tableName);
                const subscriptionHandler = new TableSubscriptionHandler(
                    {
                        globalKeys: new Set(),
                        columnsInDirectRows: new Map([[rowIndexRootPath.rest.key, new Set([rowIndexColumnName])]]),
                        columnsInIndirectRows: new Map(),
                    },
                    this.callbacks.onDataChange
                );

                const subscriptionPath = addSubscriptionHandler(
                    defined(computationModel).ns,
                    "arrayScreenContext",
                    subscriptionHandler
                );
                const subscriptionInfo: SubscriptionInfo = {
                    handlerAndPath: { handler: subscriptionHandler, path: subscriptionPath },
                    tokens: new Set(),
                    needsParsedPath: false,
                    subsidiaries: [],
                };

                return [tableData, subscriptionInfo];
            },
        };
        this.inflatedArrayScreens.set(screenName, inflatedScreen);
        return inflatedScreen;
    }

    public inflateUserProfile(): InflatedUserProfile | undefined {
        if (this.inflatedUserProfile !== undefined) return this.inflatedUserProfile;

        const ib = this.makeInflationBackend(dummyUserProfileTables, undefined);
        if (ib === undefined) return undefined;

        const info = getUserProfileTableInfo(this.appDescription);

        const verifiedEmailPath = this.computationModel?.getVerifiedEmailAddressPath();
        if (verifiedEmailPath === undefined) return undefined;

        let inflated: InflatedUserProfile = {
            emailValueGetter: hb => hb.getGlobalValue(undefined, verifiedEmailPath),
            nameValueGetter: undefined,
        };

        if (info !== undefined) {
            const [nameGetter, nameType] = ib.getValueGetterForColumn(
                { kind: SourceColumnKind.UserProfile, name: [info.nameColumnName] },
                false,
                true
            );
            if (isStringTypeKind(nameType.kind)) {
                inflated = { ...inflated, nameValueGetter: nameGetter };
            }
        }

        this.inflatedUserProfile = inflated;
        return inflated;
    }

    public inflateInternalTabs(): readonly InternalTabHydrator[] {
        if (this.inflatedInternalTabs !== undefined) {
            return this.inflatedInternalTabs;
        }

        const appTabs = getAppTabs(this.appDescription);

        const userProfileTable = definedMap(this.context.userProfileTableInfo?.tableName, tn =>
            this.context.findTable(tn)
        );

        let ib: InflationBackend | undefined;
        if (userProfileTable !== undefined) {
            ib = this.makeInflationBackend(
                { ...makeInputOutputTables(userProfileTable), containingScreen: undefined },
                undefined
            );
        }

        this.inflatedInternalTabs = appTabs.map(tab => {
            const tabScreenName = getScreenProperty(tab.screenName);
            let predicate: WirePredicate;
            if (tab.hidden === true) {
                predicate = () => false;
            } else {
                const filters = filterType(
                    tab.visibilityFilters ?? [],
                    (t): t is FilterArrayTransform => t.kind === ArrayTransformKind.Filter
                );
                predicate = inflateFilters(ib, filters);
            }

            return hb => {
                return {
                    isVisible: predicate(hb),
                    visibilityPredicate: predicate,
                    visibilitySubscriptionInfo: hb.subscribe(this.callbacks.onDataChange),
                    tabScreenName: tabScreenName ?? "Missing tab screen name",
                };
            };
        });
        return this.inflatedInternalTabs;
    }
}
